import type { FC, ReactNode} from 'react';
import { createContext, useContext } from 'react';
import useAuthInternal from '@/features/auth/hooks/useAuth';
import type { UseAuthReturn } from '@/features/auth/hooks/useAuth';

interface AuthProviderProps {
  children: ReactNode;
}

// Create the context
const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

/**
 * AuthProvider - Centralizes authentication state management
 *
 * This provider wraps the application and ensures that authentication logic
 * is only executed once at the root level, rather than in every component
 * that needs auth data. This significantly improves performance and prevents
 * redundant API calls on page refresh.
 */
export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  // Call useAuthInternal once at the root level
  // This creates the single auth instance for the entire app
  const auth = useAuthInternal();

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

/**
 * useAuthContext - Hook to access authentication context
 *
 * This replaces direct calls to useAuth() throughout the application.
 * It provides the same interface but accesses the centralized auth state.
 *
 * @throws Error if used outside of AuthProvider
 */
export const useAuthContext = (): UseAuthReturn => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};